import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { DEFAULT_TIME_ZONE, timeZoneLabel, todayInZone, formatHmInZone } from '../lib/workerTime';

const TYPES = [
  { value: 'entrada', label: 'Entrada' },
  { value: 'salida', label: 'Salida' },
  { value: 'pausaInicio', label: 'Inicio pausa' },
  { value: 'pausaFin', label: 'Fin pausa' },
];

// "Hoy" y "ahora" en la zona del TRABAJADOR (la fecha/hora elegidas se
// convierten a instante en esa misma zona en el padre).
const todayStr = (tz = DEFAULT_TIME_ZONE) => todayInZone(tz);
const nowHM = (tz = DEFAULT_TIME_ZONE) => formatHmInZone(Date.now(), tz);

// `timeZone` (opcional): zona del trabajador que ficha. Por defecto Madrid.
const ManualEntryModal = ({ open, onClose, onSubmit, initialValues = null, notice = null, timeZone = null }) => {
  const tz = timeZone || DEFAULT_TIME_ZONE;
  const tzHint = timeZoneLabel(tz);
  const [tipo, setTipo] = useState(initialValues?.tipo || 'entrada');
  const [fecha, setFecha] = useState(initialValues?.fecha || todayStr(tz));
  const [hora, setHora] = useState(initialValues?.hora || nowHM(tz));
  const [horaFin, setHoraFin] = useState(nowHM(tz));
  const [nota, setNota] = useState('');
  const [submitError, setSubmitError] = useState(null);

  // Cada vez que se reabre el modal, re-sembramos los valores. Sin esto, si
  // el padre cambia initialValues entre aperturas (p.ej. otro día anterior
  // incompleto), el formulario seguiría con los del primer render.
  useEffect(() => {
    if (!open) return;
    setTipo(initialValues?.tipo || 'entrada');
    setFecha(initialValues?.fecha || todayStr(tz));
    setHora(initialValues?.hora || nowHM(tz));
    setNota('');
    setSubmitError(null);
  }, [open, initialValues?.tipo, initialValues?.fecha, initialValues?.hora, tz]);

  if (!open) return null;

  const today = todayStr(tz);
  const isFuture = (() => {
    if (!fecha) return false;
    if (fecha > today) return true;
    if (fecha === today && hora && hora > nowHM(tz)) return true;
    return false;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (isFuture) {
      setSubmitError('No puedes registrar fichajes en el futuro. La hora aún no ha llegado.');
      return;
    }
    try {
      await onSubmit({
        tipo,
        fecha,
        hora,
        horaFin: tipo === 'pausa' ? horaFin : null,
        nota: nota.trim() || null,
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      });
      setNota('');
      onClose();
    } catch (err) {
      setSubmitError(err.message || 'Error al guardar.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="font-display text-lg font-bold text-ink">Añadir fichaje manual</h2>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {notice && (
            <div className="flex items-start gap-2 bg-warning/10 border border-warning/30 rounded-lg px-3 py-2">
              <AlertTriangle size={14} className="text-warning shrink-0 mt-0.5" />
              <div className="text-xs text-ink font-semibold">{notice}</div>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Tipo</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTipo(t.value)}
                  className={`py-2 px-3 rounded-lg text-sm font-semibold border transition-colors ${
                    tipo === t.value
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-ink border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Fecha</label>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                max={today}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Hora</label>
              <input
                type="time"
                value={hora}
                onChange={(e) => setHora(e.target.value)}
                max={fecha === today ? nowHM(tz) : undefined}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                required
              />
              {tzHint && <div className="text-[11px] text-ink-muted mt-1">{tzHint}</div>}
              <div className="text-[11px] text-ink-muted mt-1">Sólo se aceptan fichajes pasados o presentes — no se puede fichar en el futuro.</div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Nota (opcional)</label>
            <input
              type="text"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Ej. Reunión con cliente"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
            />
          </div>

          {submitError && (
            <div className="text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2">
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors"
            >
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualEntryModal;
